
import React, { useState } from 'react';
import { Language, Theme } from '../types';
import { ValleyOfTasteLogo, SunIcon, MoonIcon } from './icons';
import { t } from '../translations';
import { useTheme } from '../contexts/ThemeContext';
import { motion } from 'framer-motion';

interface WelcomeProps {
    onComplete: (language: Language, theme: Theme) => void;
}

const languages: { code: Language; name: string }[] = [
    { code: 'ky', name: 'Кыргызча' },
    { code: 'ru', name: 'Русский' },
    { code: 'zh', name: '中文' },
    { code: 'en', name: 'English' },
];

const Welcome: React.FC<WelcomeProps> = ({ onComplete }) => {
    const { theme } = useTheme();
    const [selectedLanguage, setSelectedLanguage] = useState<Language>('ky');
    const [selectedTheme, setSelectedTheme] = useState<Theme>(theme);

    const handleThemeToggle = () => {
        setSelectedTheme(prev => prev === 'light' ? 'dark' : 'light');
    };
    
    return (
        <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-[var(--bg-primary)]"
        >
            <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="glass-effect w-full max-w-md squircle shadow-[var(--shadow-medium)] p-8 text-center border border-[var(--glass-border)]"
            >
                
                <div className="w-20 h-20 bg-[var(--bg-tertiary)] rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                    <ValleyOfTasteLogo className="h-10 w-10 text-[var(--accent-color)]" />
                </div>

                {/* Разделено на две строки: приветствие и бренд-строку.
                    Раньше "Добро пожаловать в Долину Вкуса" ломалось на 3 строки на мобиле. */}
                <p className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-widest mb-1">
                    {t('welcome.title', selectedLanguage)}
                </p>
                {/* Letter-by-letter fade-in для брендового заголовка.
                    key={selectedLanguage} перезапускает анимацию при смене языка,
                    чтобы новый текст тоже «проявился» буквами, а не resync. */}
                <motion.h1
                    key={selectedLanguage}
                    className="text-3xl font-bold font-serif text-[var(--text-primary)] mb-3 leading-tight"
                    aria-label={t('welcome.brand', selectedLanguage)}
                >
                    {Array.from(t('welcome.brand', selectedLanguage)).map((ch, i) => (
                        <motion.span
                            key={`${selectedLanguage}-${i}`}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.25 + i * 0.06, duration: 0.35 }}
                            style={{ display: 'inline-block', whiteSpace: 'pre' }}
                            aria-hidden
                        >
                            {ch}
                        </motion.span>
                    ))}
                </motion.h1>
                <p className="text-[var(--text-secondary)] mb-8 text-sm">
                    {t('welcome.subtitle', selectedLanguage)}
                </p>

                <div className="space-y-6">
                    <div>
                        <h3 className="font-bold text-[var(--text-secondary)] text-xs uppercase tracking-widest mb-4">{t('welcome.language', selectedLanguage)}</h3>
                        <div className="grid grid-cols-2 gap-3">
                            {languages.map(lang => (
                                <button
                                    key={lang.code}
                                    onClick={() => setSelectedLanguage(lang.code)}
                                    className={`px-4 py-4 rounded-2xl transition-all duration-200 text-sm font-bold shadow-sm ${
                                        selectedLanguage === lang.code
                                            ? 'bg-[var(--accent-color)] text-[var(--accent-text)] ring-2 ring-offset-2 ring-[var(--accent-color)] ring-offset-[var(--bg-card)]'
                                            : 'bg-[var(--bg-tertiary)] text-[var(--text-primary)] hover:bg-opacity-80'
                                    }`}
                                >
                                    {lang.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h3 className="font-bold text-[var(--text-secondary)] text-xs uppercase tracking-widest mb-4">{t('welcome.theme', selectedLanguage)}</h3>
                        <div className="flex justify-center items-center bg-[var(--bg-tertiary)] rounded-2xl p-1.5 max-w-xs mx-auto">
                            <button 
                                onClick={() => setSelectedTheme('light')}
                                className={`w-1/2 flex items-center justify-center space-x-2 py-3 rounded-xl transition-all duration-200 text-sm font-bold ${selectedTheme === 'light' ? 'bg-[var(--bg-card)] text-[var(--text-primary)] shadow-md' : 'text-[var(--text-secondary)]'}`}
                            >
                                <SunIcon className="w-5 h-5"/>
                                <span>{t('welcome.light', selectedLanguage)}</span>
                            </button>
                            <button 
                                onClick={() => setSelectedTheme('dark')}
                                className={`w-1/2 flex items-center justify-center space-x-2 py-3 rounded-xl transition-all duration-200 text-sm font-bold ${selectedTheme === 'dark' ? 'bg-[var(--bg-card)] text-[var(--text-primary)] shadow-md' : 'text-[var(--text-secondary)]'}`}
                            >
                                <MoonIcon className="w-5 h-5"/>
                                <span>{t('welcome.dark', selectedLanguage)}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <button
                    onClick={() => onComplete(selectedLanguage, selectedTheme)}
                    className="mt-12 w-full px-10 py-4 bg-[var(--accent-color)] text-[var(--accent-text)] font-bold text-lg rounded-2xl shadow-[var(--shadow-medium)] hover:brightness-110 transition-all duration-300 transform active:scale-95"
                >
                    {t('welcome.button', selectedLanguage)}
                </button>
            </motion.div>
        </motion.div>
    );
};

export default Welcome;
